import { useEffect } from 'react';
import GoalCard from './GoalCard';
import GoalCardSkeleton from './GoalCardSkeleton';
import DotPagination from './DotPagination';

export interface Goal {
    goal_id: number;
    goal_name: string;
    goal_status: string;
    end_date: string;
    employee_task_count: number;
    employee_actual_hours: number;
    skill_name?: string;
    climb_type?: string;
}

interface Pagination {
    total_pages: number;
    current_page: number;
    total_items: number;
    has_next: boolean;
    has_previous: boolean;
}

interface GoalsSectionProps {
    goals: Goal[];
    selectedSkill: string | null;
    loading: boolean;
    error: any;
    pagination: Pagination | null;
    currentPage: number;
    onPageChange: (page: number) => void;
    labelType?: string;
}

const GoalsSection = ({ goals, selectedSkill, loading, error, pagination, currentPage, onPageChange, labelType = 'Skill' }: GoalsSectionProps) => {
    // Reset to first page when selected skill changes
    useEffect(() => {
        onPageChange(1);
    }, [selectedSkill, onPageChange]);

    return (
        <div className="bg-surface-bg-primary rounded-lg p-6 border border-surface-border">
            <div className="flex items-center gap-2 mb-4 justify-between">
                <h3 className="text-lg font-semibold">Goals</h3>
                {selectedSkill && (
                    <span className="text-xs px-2 py-1 rounded bg-main-blue text-white btn-goal-project">
                        + {selectedSkill}
                    </span>
                )}
            </div>
            {loading ? (
                <div className="flex flex-col gap-3">
                    <GoalCardSkeleton />
                    <GoalCardSkeleton />
                    <GoalCardSkeleton />
                </div>
            ) : error ? (
                <div className="text-center py-8 text-danger-active">
                    Error loading goals
                </div>
            ) : goals.length === 0 ? (
                <div className="text-center py-8 text-text-text-secondary">
                    No goals found for this skill
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-3">
                        {goals.map((goal) => (
                            <GoalCard
                                key={goal.goal_id}
                                goalName={goal.goal_name}
                                goalStatus={goal.goal_status}
                                endDate={goal.end_date}
                                employeeTaskCount={goal.employee_task_count}
                                employeeActualHours={goal.employee_actual_hours}
                            />
                        ))}
                    </div>
                    {pagination && pagination.total_pages > 1 && (
                        <DotPagination
                            totalPages={pagination.total_pages}
                            currentPage={currentPage - 1}
                            onPageChange={(page) => onPageChange(page + 1)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default GoalsSection;
